import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { ImageCapture } from "../components/ImageCapture";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Briefcase, Plus, MapPin, Trash2, Mail, ImageOff, Crosshair, ExternalLink, Search, X } from "lucide-react";

const BE = process.env.REACT_APP_BACKEND_URL;

const JOB_CATEGORIES = [
  "General labor", "Moving & delivery", "Cleaning", "Handyman & repair",
  "Yard & landscaping", "Construction & trades", "Food & hospitality",
  "Retail & sales", "Childcare", "Pet care", "Tutoring & education",
  "Creative & design", "Tech & IT", "Admin & office", "Driving & transport",
  "Events & staffing", "Beauty & wellness", "Other",
];

function payLabel(j) {
  if (j.payAmount == null || j.payAmount === "") return "Pay: ask";
  const a = Number(j.payAmount).toLocaleString();
  if (j.payType === "hourly") return `$${a}/hr`;
  if (j.payType === "salary") return `$${a}/yr salary`;
  return `$${a} one-time`;
}

export default function Jobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  const [form, setForm] = useState({ title: "", category: "General labor", location: "", lat: null, lng: null, payType: "hourly", payAmount: "", description: "", image_base64: null });

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const fld = (k) => ({ value: form[k], onChange: (e) => setForm((f) => ({ ...f, [k]: e.target.value })) });

  const useGps = () => {
    if (!navigator.geolocation) { toast.error("GPS not available on this device"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: pos.coords.latitude, lng: pos.coords.longitude })); toast.success("GPS location captured"); },
      () => toast.error("Could not get your GPS location"),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const post = async () => {
    if (!form.title.trim()) { toast.error("Give the job a title"); return; }
    setBusy(true);
    try {
      const body = { ...form, payAmount: form.payAmount === "" ? null : Number(form.payAmount) };
      await api.post("/jobs", body);
      toast.success("Job posted");
      setCreating(false);
      setForm({ title: "", category: "General labor", location: "", lat: null, lng: null, payType: "hourly", payAmount: "", description: "", image_base64: null });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this job offer?")) return;
    try { await api.delete(`/jobs/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const q = query.trim().toLowerCase();
  const filtered = (jobs || []).filter((j) => {
    if (cat && (j.category || "Other") !== cat) return false;
    if (!q) return true;
    return [j.title, j.description, j.location, j.category]
      .some((v) => (v || "").toLowerCase().includes(q));
  });
  const activeCats = JOB_CATEGORIES.filter((c) => (jobs || []).some((j) => (j.category || "Other") === c));

  return (
    <Layout>
      <div className="space-y-6" data-testid="jobs-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-mono flex items-center gap-2"><Briefcase className="w-4 h-4" /> Job offers</div>
            <h1 className="font-head font-bold text-2xl sm:text-3xl mt-1">Jobs from the community</h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Offer work to others on Path Mentor, or find a gig near you.</p>
          </div>
          {user?.paid
            ? <button onClick={() => setCreating(!creating)} className="pm-btn flex items-center gap-2 text-sm" data-testid="post-job-btn"><Plus className="w-4 h-4" /> Post a job</button>
            : <Link to="/pricing" className="pm-btn-ghost text-sm" data-testid="post-job-upgrade">Upgrade to post a job</Link>}
        </div>

        {creating && user?.paid && (
          <div className="pm-card p-5 space-y-3" data-testid="job-create-form">
            <input className="pm-input" placeholder="Job title (e.g. Weekend mover, Part-time barista)" {...fld("title")} data-testid="job-title-input" />
            <select className="pm-input" {...fld("category")} data-testid="job-category-input">
              {JOB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className="pm-input" placeholder="Location (city, area)" {...fld("location")} data-testid="job-location-input" />
              <button onClick={useGps} className="pm-btn-ghost text-sm flex items-center justify-center gap-2" data-testid="job-gps-btn">
                <Crosshair className="w-4 h-4" /> {form.lat != null ? "GPS captured ✓" : "Use my GPS location"}
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <select className="pm-input" {...fld("payType")} data-testid="job-paytype-input">
                <option value="hourly">Hourly</option>
                <option value="salary">Salary (per year)</option>
                <option value="lump">One lump pay</option>
              </select>
              <input className="pm-input" type="number" placeholder="Pay amount (USD)" {...fld("payAmount")} data-testid="job-payamount-input" />
            </div>
            <textarea className="pm-input" rows={3} placeholder="Describe the job, hours, what's needed…" {...fld("description")} data-testid="job-description-input" />
            <div>
              <div className="label-mono mb-1">Photo (optional)</div>
              {form.image_base64
                ? <div className="flex items-center gap-3"><img src={`data:image/jpeg;base64,${form.image_base64}`} alt="job" className="w-24 h-24 object-cover rounded" /><button onClick={() => { setForm((f) => ({ ...f, image_base64: null })); setPhotoKey((k) => k + 1); }} className="pm-btn-ghost text-xs">Remove photo</button></div>
                : <ImageCapture key={photoKey} onImage={(b) => setForm((f) => ({ ...f, image_base64: b }))} />}
            </div>
            <div className="flex gap-2">
              <button onClick={post} disabled={busy} className="pm-btn text-sm" data-testid="job-submit-btn">{busy ? "Posting…" : "Post job"}</button>
              <button onClick={() => setCreating(false)} className="pm-btn-ghost text-sm">Cancel</button>
            </div>
          </div>
        )}

        {jobs && jobs.length > 0 && (
          <div className="space-y-3" data-testid="jobs-filters">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-2)" }} />
              <input
                className="pm-input pl-9 pr-9"
                placeholder="Search jobs by title, place or keyword…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="job-search-input"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="job-search-clear" aria-label="Clear search">
                  <X className="w-4 h-4" style={{ color: "var(--text-2)" }} />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="job-category-chips">
              <button
                onClick={() => setCat("")}
                className="badge"
                style={cat === "" ? { background: "var(--earn)", color: "#fff" } : { background: "var(--surface-subtle)", cursor: "pointer" }}
                data-testid="job-cat-all"
              >All categories</button>
              {activeCats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c === cat ? "" : c)}
                  className="badge"
                  style={c === cat ? { background: "var(--earn)", color: "#fff" } : { background: "var(--surface-subtle)", cursor: "pointer" }}
                  data-testid={`job-cat-${c.split(" ")[0].toLowerCase()}`}
                >{c}</button>
              ))}
            </div>
          </div>
        )}

        {jobs && jobs.length === 0 && (
          <div className="pm-card p-8 text-center" data-testid="jobs-empty">
            <p className="text-sm" style={{ color: "var(--text-2)" }}>No job offers yet. {user?.paid ? "Be the first to post one." : "Check back soon."}</p>
          </div>
        )}

        {jobs && jobs.length > 0 && filtered.length === 0 && (
          <div className="pm-card p-8 text-center" data-testid="jobs-no-match">
            <p className="text-sm" style={{ color: "var(--text-2)" }}>No jobs match your search. <button onClick={() => { setQuery(""); setCat(""); }} className="underline" data-testid="jobs-reset-filters">Clear filters</button></p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3" data-testid="jobs-list">
          {filtered.map((j) => (
            <div key={j.id} className="pm-card p-0 overflow-hidden flex flex-col" data-testid={`job-card-${j.id}`}>
              {j.photo
                ? <img src={`${BE}${j.photo.url}`} alt={j.title} className="w-full h-40 object-cover" />
                : <div className="w-full h-24 flex items-center justify-center" style={{ background: "var(--surface-subtle)" }}><ImageOff className="w-6 h-6" style={{ color: "var(--text-2)" }} /></div>}
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-head font-bold text-lg leading-tight">{j.title}</h3>
                  <span className="badge whitespace-nowrap" style={{ background: "var(--earn)", color: "#fff" }}>{payLabel(j)}</span>
                </div>
                <div className="mt-1.5">
                  <span className="badge" style={{ background: "var(--surface-subtle)" }} data-testid={`job-cat-badge-${j.id}`}>{j.category || "Other"}</span>
                </div>
                {(j.location || j.lat != null) && (
                  <div className="label-mono mt-1 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> {j.location || "GPS pinned"}
                    {j.lat != null && <a href={`https://www.google.com/maps?q=${j.lat},${j.lng}`} target="_blank" rel="noreferrer" className="underline flex items-center gap-0.5" data-testid={`job-map-${j.id}`}>map <ExternalLink className="w-3 h-3" /></a>}
                  </div>
                )}
                {j.description && <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{j.description}</p>}
                <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border-subtle)" }}>
                  <a href={`mailto:${j.posterEmail}?subject=${encodeURIComponent("About your job: " + j.title)}`} className="pm-btn-ghost text-xs flex items-center gap-1.5" data-testid={`job-contact-${j.id}`}><Mail className="w-3.5 h-3.5" /> Contact poster</a>
                  {j.isMine && <button onClick={() => remove(j.id)} className="pm-btn-ghost text-xs flex items-center gap-1.5 ml-auto" data-testid={`job-delete-${j.id}`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
